import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { invoicesAPI } from '@/lib/api-client'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { InvoiceUploadDialog } from './invoices/InvoiceUploadDialog'
import {
  Receipt,
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react'

const STATUS_LABELS: Record<string, string> = {
  PROCESSING: 'Processando',
  REVIEW: 'Em Revisao',
  CONFIRMED: 'Confirmada',
  LINKED: 'Vinculada',
  CANCELLED: 'Cancelada',
}

const STATUS_COLORS: Record<string, string> = {
  PROCESSING: 'bg-blue-100 text-blue-700',
  REVIEW: 'bg-yellow-100 text-yellow-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  LINKED: 'bg-purple-100 text-purple-700',
  CANCELLED: 'bg-neutral-100 text-neutral-500',
}

const TYPE_LABELS: Record<string, string> = {
  OBRA: 'Obra',
  OPERACIONAL: 'Operacional',
}

export function Invoices() {
  const navigate = useNavigate()
  const canCreate = usePermission('invoices:create')

  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: [
      'invoices',
      { page, search, status: statusFilter, type: typeFilter },
    ],
    queryFn: () =>
      invoicesAPI.list({
        page,
        limit: 15,
        search: search || undefined,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
      }),
  })

  const invoices = data?.data || []
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-neutral-900">Notas Fiscais</h1>
          <Badge variant="secondary" className="text-sm">
            {pagination.total}
          </Badge>
        </div>
        {canCreate && (
          <Button onClick={() => setShowUploadDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Nota
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row flex-wrap">
        <form onSubmit={handleSearch} className="flex flex-1 gap-2 min-w-[300px]">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar por numero da NF ou fornecedor..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button type="submit" variant="secondary">
            Buscar
          </Button>
        </form>

        <Select
          value={statusFilter || 'ALL'}
          onValueChange={(value) => {
            setStatusFilter(value === 'ALL' ? '' : value)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os status</SelectItem>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={typeFilter || 'ALL'}
          onValueChange={(value) => {
            setTypeFilter(value === 'ALL' ? '' : value)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os tipos</SelectItem>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-16">
          <Receipt className="h-16 w-16 text-neutral-300" />
          <h3 className="mt-4 text-xl font-medium text-neutral-900">
            {search || statusFilter || typeFilter
              ? 'Nenhuma nota fiscal encontrada'
              : 'Nenhuma nota fiscal'}
          </h3>
          <p className="mt-2 text-neutral-500">
            {search || statusFilter || typeFilter
              ? 'Tente ajustar os filtros de busca.'
              : 'Comece enviando sua primeira nota fiscal.'}
          </p>
          {canCreate && !search && !statusFilter && !typeFilter && (
            <Button className="mt-6" onClick={() => setShowUploadDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Enviar Primeira Nota
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-3 sm:mx-0">
          <div className="min-w-[580px] sm:min-w-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>NF</TableHead>
                <TableHead>Fornecedor</TableHead>
                <TableHead>Projeto</TableHead>
                <TableHead>Data Emissao</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Valor Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice: any) => (
                <TableRow
                  key={invoice.id}
                  className="cursor-pointer hover:bg-neutral-50"
                  onClick={() => navigate(`/invoices/${invoice.id}`)}
                >
                  <TableCell className="font-medium">
                    {invoice.invoiceNumber || 'Sem numero'}
                  </TableCell>
                  <TableCell>
                    {invoice.supplier?.name || invoice.ocrSupplierName || '-'}
                  </TableCell>
                  <TableCell>{invoice.project?.name || '-'}</TableCell>
                  <TableCell>
                    {invoice.issueDate
                      ? new Date(invoice.issueDate).toLocaleDateString('pt-BR')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_COLORS[invoice.status]}>
                      {STATUS_LABELS[invoice.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {invoice.totalValue != null
                      ? `R$ ${Number(invoice.totalValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-neutral-500">
                Mostrando {invoices.length} de {pagination.total} notas fiscais
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <span className="text-sm text-neutral-600">
                  {page} / {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Proximo
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Upload Dialog */}
      <InvoiceUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        onSuccess={(newInvoice: any) => {
          setShowUploadDialog(false)
          if (newInvoice?.id) {
            navigate(`/invoices/${newInvoice.id}`)
          }
        }}
      />
    </div>
  )
}
